import React from 'react';
import Helmet from 'react-helmet';
import Raven from 'raven-js';
import Alert from 'react-s-alert';

import Avatar from 'api/resources/avatar';
import Medialib from 'api/resources/medialib';
import getPrefix from 'utils/getPrefix';
import { processErrorByAlert } from 'utils/getErrors';
import { Deserializer } from 'jsonapi-serializer';
import getFileName from 'utils/getFileName';

import Main from 'styled/Page/Main';
import Header from 'styled/Page/Header';
import HeaderTitle from 'styled/Page/HeaderTitle';
import CenteredContainer from 'styled/Page/CenteredContainer';
import RightColumn from 'styled/Page/RightColumn';
import PrimaryButton from 'styled/Buttons/PrimaryButton';
import ButtonLoader from 'components/Loader/ButtonLoader';

import { ReactComponent as IconPhone } from 'icons/PhoneWithChat.svg';

import AvatarImage from './AvatarImage';
import AvatarWrapper from './AvatarWrapper';

const MIN_IMAGE_SIZE = 960;

class AvatarPage extends React.PureComponent {
  hideImage = React.createRef();

  input = React.createRef();

  state = {
    loading: false,
    avatar: undefined,
    updateLoading: false,
  }

  componentDidMount() {
    this.loadAvatar();
  }

  componentWillUnmount() {
    if (this.abortController) this.abortController.abort();
  }

  loadAvatar = async () => {
    if (this.abortController) this.abortController.abort();
    if (typeof window.AbortController === 'function') {
      this.abortController = new window.AbortController();
    }

    this.setState({ loading: true });

    try {
      const response = await Avatar.get({
        include: 'image',
        signal: this.abortController ? this.abortController.signal : undefined,
      });

      if (!response.data) {
        this.setState({ loading: false });
        return;
      }

      const avatar = await new Deserializer({ keyForAttribute: 'camelCase' }).deserialize(response);

      this.setState({ avatar, loading: false });
    } catch (err) {
      this.setState({ loading: false });
      if (err.name === 'AbortError') return;

      processErrorByAlert(err);
      Raven.captureException(err, { extra: this.props });
    }
  }

  handleFileChange = (e) => {
    if (!e.target.files[0]) return;

    if (typeof (FileReader) !== 'function') {
      Alert.error('Загрузка картинок не доступна в вашем браузере');
      return;
    }

    if (!e.target.files || e.target.files.length === 0) {
      Alert.error('Ошибка загрузки');
      return;
    }

    const file = e.target.files[0];
    // для повторной загрузки одной и той же картинки
    this.input.current.value = '';

    if (file.type !== 'image/png') {
      Alert.error('Неподдерживаемый тип изображения');
      return;
    }

    this.onLoad = () => { this.checkSize(file); };

    this.hideImage.current.addEventListener('load', this.onLoad);

    const fileReader = new FileReader();
    fileReader.onload = () => {
      this.hideImage.current.src = fileReader.result;
    };

    fileReader.readAsDataURL(file);
  }

  checkSize = (file) => {
    this.hideImage.current.removeEventListener('load', this.onLoad);

    if (this.hideImage.current.clientWidth >= MIN_IMAGE_SIZE && this.hideImage.current.height >= MIN_IMAGE_SIZE) {
      this.loadImage(file);
    } else {
      Alert.error(`Размер загруженного изображения меньше ${MIN_IMAGE_SIZE}х${MIN_IMAGE_SIZE}px`);
    }
  }

  loadImage = async (file) => {
    this.setState({ updateLoading: true });

    try {
      const response = await Medialib.post({
        upload: file,
        prefix: getPrefix('avatar'),
      });

      const files = await new Deserializer({ keyForAttribute: 'camelCase' }).deserialize(response);

      this.createAvatar(files);
    } catch (err) {
      this.setState({ updateLoading: false });
      processErrorByAlert(err);

      Raven.captureException(err, { extra: this.state });
    }
  }

  createAvatar = async (files) => {
    if (!files || files.length === 0) {
      this.setState({ updateLoading: false });
      Alert.error('Не удалось загрузить аватар');
      return;
    }

    try {
      const response = await Avatar.create({
        include: 'image',
        body: {
          data: {
            type: 'avatar',
            relationships: {
              image: {
                data: {
                  type: 'image',
                  attributes: {
                    filename: getFileName({
                      url: files[0].path,
                      type: '/avatar',
                    }),
                    file_type: files[0].contentType,
                    file_size: files[0].size,
                    height: files[0].height,
                    width: files[0].width,
                    url: files[0].path,
                    medialib_item_id: files[0].id,
                  },
                },
              },
            },
          },
        },
      });

      const avatar = await new Deserializer({ keyForAttribute: 'camelCase' }).deserialize(response);

      this.setState({ avatar, updateLoading: false });
      Alert.success('Аватар приложения изменен');
    } catch (err) {
      this.setState({ updateLoading: false });
      processErrorByAlert(err);
      Raven.captureException(err, { extra: files });
    }
  }

  render() {
    return (
      <Main>
        <Helmet><title>Аватар приложения</title></Helmet>

        <img ref={this.hideImage} style={{ position: 'absolute', top: '-3000px' }} alt="hideImage" />

        <div style={{ width: '100%' }}>
          <Header style={{ alignItems: 'center', marginTop: '15px' }}>
            <HeaderTitle style={{ paddingBottom: 0 }}>
              аватар приложения
            </HeaderTitle>

            <PrimaryButton
              as="label"
              style={{ marginLeft: 'auto' }}
              disabled={this.state.loading || this.state.updateLoading}
            >
              {!this.state.updateLoading && 'загрузить аватар'}

              {this.state.updateLoading && <ButtonLoader />}

              <input
                type="file"
                ref={this.input}
                style={{ display: 'none' }}
                onChange={this.handleFileChange}
                accept="image/png"
                disabled={this.state.loading || this.state.updateLoading}
              />
            </PrimaryButton>
          </Header>

          <CenteredContainer style={{ paddingTop: '32px', position: 'relative' }}>
            <IconPhone />

            <AvatarWrapper>
              {!this.state.loading && this.state.avatar && this.state.avatar.image && this.state.avatar.image.url && (
                <AvatarImage src={this.state.avatar.image.url} />
              )}

              {(this.state.loading) && (<ButtonLoader />)}
            </AvatarWrapper>
          </CenteredContainer>
        </div>

        <RightColumn />
      </Main>
    );
  }
}

export default AvatarPage;
